import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./BystanderPaywall.module.scss"

/**
 * BystanderPaywall (`/bystander`) — an original, self-contained NON-ONBOARDING
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for
 * this run: NON-ONBOARDING (an existing user, mid-session, prompted to upgrade).
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority and the entire concept is built to it. Security & privacy,
 * told from the one angle none of the 90+ prior concepts take: not YOUR view in,
 * but the OTHER person's view — the moment your phone leaves your hand.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "In anyone else's hands, there's nothing to find." Your warm, whole photo
 * library lives inside the vault. Hand the phone to someone else and the same
 * screen tells a completely different, deliberately dull truth: the vault isn't
 * even there — it's a plain calculator, and a curious tap opens only a harmless
 * decoy. You drag the HANDOVER control from "You" to "Anyone else" and watch the
 * two realities cross-fade. Privacy proven by contrast: the value isn't a wall
 * you hide behind, it's that there is simply nothing for a stranger to find.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the security cluster) ─────
 * Deliberately far from every security neighbour (Sanctum rings, Static cipher-
 * noise, Eyes Only redaction, Safelight darkroom, Cipher, Ward crest, Only You
 * keypad, In Plain Sight disguise-flip, Reflex face-down, Vault Door, Keyholder,
 * Tumbler dial, Perimeter radar, Imprint fingerprint, Glyph, Vigil, Failsafe,
 * Caught CCTV, Decoy):
 * Layout      : ONE STAGE, TWO REALITIES — a single device that MORPHS between
 *               two complete states (your warm vault ⇄ a stranger's dead-end
 *               calculator). Not a card list, ring, wall, dossier, crest, door,
 *               keypad or console.
 * Interaction : HANDOVER — a slider you push from "You" to "Anyone else". A
 *               perspective switch, not a decrypt/hold/dial/scrub. Dragging lets
 *               you PEEK the morph at any point between the two truths.
 * Metaphor    : "IN SOMEONE ELSE'S HANDS" — the same phone, two different
 *               truths, and only yours is real.
 * Motion      : the real vault DESATURATES + SCALES AWAY while the calculator
 *               disguise assembles button-by-button over it; warmth drains to
 *               cool grey and floods back. Not a reveal of hidden pixels.
 * Feature     : the four real safeguards ARE what makes "nothing to find" true —
 *               each chip momentarily throws the stage to the stranger's view to
 *               show its behaviour. No bolted-on bullet list.
 * Value frame : CONTRAST + risk-reversal reassurance — not "hide from a threat"
 *               but "there is nothing here for anyone but you".
 * Register    : calm, quietly confident, a little playful — relief, never fear.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard shown is a REAL Private Photo Vault feature: only-you lock
 * (Face ID / Touch ID / PIN), App disguise (the vault looks like an ordinary
 * calculator), Decoy Vault (a second code opens a harmless empty vault), and
 * Break-in snapshots (a silent photo on a wrong code). Nothing here is at stake
 * — this is a NON-ONBOARDING upgrade, so there is NO data-loss threat, NO
 * countdown, NO fabricated scarcity. "Save 79%" is real arithmetic from live
 * product data. Yearly is the featured, default, most prominent price; monthly
 * is small & secondary. The 7-day free trial and billing terms read from real
 * products. Renders on the built-in debug data with bundled photos — no native
 * injection, no external egress.
 */

// A warm, personal set drawn from bundled assets — the life inside the vault.
const PHOTOS = [
  "/recall/beach-1.jpg",
  "/recall/dog-1.jpg",
  "/recall/coffee-1.jpg",
  "/recall/sunset-1.jpg",
  "/recall/city-1.jpg",
  "/recall/mountain-1.jpg",
  "/recall/food-1.jpg",
  "/recall/flower-1.jpg",
  "/recall/coffee-2.jpg",
]

// The four real safeguards that make "nothing to find" the honest truth.
const GUARDS = [
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    they: "They see a plain calculator — the vault isn't on this phone.",
  },
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    they: "Face ID, Touch ID, or a code only you know opens it.",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    they: "A second code opens a harmless, empty vault — never yours.",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    they: "One wrong code and a silent photo of them is saved for you.",
  },
]

// Calculator disguise face (App disguise). Purely cosmetic — the point is that
// a stranger's phone looks utterly ordinary.
const CALC_KEYS = [
  "C",
  "±",
  "%",
  "÷",
  "7",
  "8",
  "9",
  "×",
  "4",
  "5",
  "6",
  "−",
  "1",
  "2",
  "3",
  "+",
  "0",
  ".",
  "=",
]

const DEMO_TO_THEM_MS = 1150
const DEMO_HOLD_MS = 1500

const BystanderPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // who is holding the phone: 0 = you, 1 = anyone else. `target` is where it
  // settles; `drag` is the live value while a pointer drags the knob.
  const [target, setTarget] = useState(0)
  const [drag, setDrag] = useState<number | null>(null)
  const [dragging, setDragging] = useState(false)
  const [focusGuard, setFocusGuard] = useState(0)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const demoTimers = useRef<number[]>([])

  const who = drag ?? target
  const isThem = who > 0.5

  // teaching demo: on arrival, hand it over once and take it back, so the payoff
  // (two realities) reads before any interaction.
  useEffect(() => {
    if (reduceMotion) return
    demoTimers.current.push(
      window.setTimeout(() => setTarget(1), DEMO_TO_THEM_MS),
    )
    demoTimers.current.push(
      window.setTimeout(() => setTarget(0), DEMO_TO_THEM_MS + DEMO_HOLD_MS),
    )
    return () => {
      demoTimers.current.forEach((t) => window.clearTimeout(t))
      demoTimers.current = []
    }
  }, [reduceMotion])

  const stopDemo = useCallback(() => {
    demoTimers.current.forEach((t) => window.clearTimeout(t))
    demoTimers.current = []
  }, [])

  const fractionFromEvent = useCallback((clientX: number) => {
    const el = trackRef.current
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    const f = (clientX - rect.left) / rect.width
    return Math.min(1, Math.max(0, f))
  }, [])

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      stopDemo()
      ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
      setDragging(true)
      setDrag(fractionFromEvent(e.clientX))
    },
    [fractionFromEvent, stopDemo],
  )

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (!dragging) return
      setDrag(fractionFromEvent(e.clientX))
    },
    [dragging, fractionFromEvent],
  )

  const endDrag = useCallback(() => {
    if (!dragging) return
    setDragging(false)
    setDrag((d) => {
      setTarget(d != null && d > 0.5 ? 1 : 0)
      return null
    })
  }, [dragging])

  const handover = useCallback(
    (to: number) => {
      stopDemo()
      setTarget(to)
    },
    [stopDemo],
  )

  const onGuard = useCallback(
    (i: number) => {
      stopDemo()
      setFocusGuard(i)
      setTarget(1)
    },
    [stopDemo],
  )

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const guard = GUARDS[focusGuard]

  return (
    <div
      className={`${styles.root} ${isThem ? styles.isThem : ""} ${
        dragging ? styles.dragging : ""
      }`}
      style={{ ["--who" as any]: who }}
    >
      <div className={styles.wash} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          Hand it over. Go ahead.
        </span>
        <h1 className={styles.title}>
          In anyone else&rsquo;s hands,
          <br />
          there&rsquo;s <span className={styles.titleAccent}>nothing</span> to
          find.
        </h1>
      </section>

      {/* ── the stage: one device, two realities ───────────────────────────── */}
      <section className={styles.stage}>
        <div className={styles.device}>
          {/* YOUR reality — the warm, whole library */}
          <div className={styles.you} aria-hidden={isThem}>
            <div className={styles.grid}>
              {PHOTOS.map((src, i) => (
                <span
                  key={src}
                  className={styles.tile}
                  style={{
                    backgroundImage: `url(${src})`,
                    ["--d" as any]: `${i}`,
                  }}
                />
              ))}
            </div>
            <div className={styles.youStatus}>
              <span className={styles.statusDot} aria-hidden="true" />
              Secured · only you
            </div>
          </div>

          {/* THEIR reality — an ordinary calculator, dead-end and dull */}
          <div className={styles.them} aria-hidden={!isThem}>
            <div className={styles.calc}>
              <div className={styles.calcApp}>Calculator</div>
              <div className={styles.calcScreen}>0</div>
              <div className={styles.calcKeys}>
                {CALC_KEYS.map((k, i) => (
                  <span
                    key={`${k}-${i}`}
                    className={`${styles.calcKey} ${
                      i === 3 || i === 7 || i === 11 || i === 15
                        ? styles.calcOp
                        : ""
                    } ${i === 16 ? styles.calcZero : ""}`}
                    style={{ ["--d" as any]: `${i}` }}
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>
            <div className={styles.themTag}>Nothing here but a calculator</div>
          </div>

          <span className={styles.notch} aria-hidden="true" />
        </div>

        {/* the handover control */}
        <div className={styles.handover}>
          <button
            className={`${styles.end} ${!isThem ? styles.endOn : ""}`}
            onClick={() => handover(0)}
            aria-label="Show what you see"
          >
            You
          </button>
          <div
            ref={trackRef}
            className={styles.track}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={1}
            aria-valuenow={isThem ? 1 : 0}
            aria-valuetext={isThem ? "Anyone else" : "You"}
            aria-label="Hand the phone over"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") handover(0)
              if (e.key === "ArrowRight") handover(1)
            }}
          >
            <span className={styles.trackFill} aria-hidden="true" />
            <span className={styles.knob} aria-hidden="true">
              <span className={styles.knobGrip} />
            </span>
          </div>
          <button
            className={`${styles.end} ${styles.endThem} ${
              isThem ? styles.endOn : ""
            }`}
            onClick={() => handover(1)}
            aria-label="Show what anyone else sees"
          >
            Anyone else
          </button>
        </div>

        <p className={styles.readout} aria-live="polite">
          {isThem
            ? "A stranger sees a plain calculator. Your vault isn't even here."
            : "You see everything — warm, whole, and yours alone."}
        </p>
      </section>

      {/* ── the four real safeguards behind "nothing to find" ──────────────── */}
      <div className={styles.guards}>
        {GUARDS.map((g, i) => (
          <button
            key={g.key}
            className={`${styles.guard} ${
              focusGuard === i && isThem ? styles.guardOn : ""
            }`}
            onClick={() => onGuard(i)}
            aria-label={`${g.name}: ${g.they}`}
          >
            <span className={styles.guardIcon} aria-hidden="true">
              <g.Icon width={18} height={18} />
            </span>
            <span className={styles.guardName}>{g.name}</span>
          </button>
        ))}
      </div>

      <p className={styles.guardLine} aria-live="polite">
        <span className={styles.guardLineDot} data-them={isThem} />
        <b>{guard.name}</b>
        <span className={styles.guardSep}>·</span>
        {guard.they}
      </p>

      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Real safeguards, already in the app. Your photos and videos never leave
        your control — and to anyone else, there&rsquo;s simply nothing there.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · full protection</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Protect my vault — start free"
              : "Protect my vault — monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default BystanderPaywall
